import { useId } from "react";

type ReadinessRingProps = {
  value?: number;
  readinessLabel?: string;
  /** Smaller ring for fixed A4 PDF pages */
  compact?: boolean;
};

export function ReadinessRing({ value = 86, readinessLabel = "High", compact = false }: ReadinessRingProps) {
  const gradientId = useId().replace(/:/g, "");
  const size = compact ? 200 : 320;
  const stroke = compact ? 12 : 14;
  const r = compact ? 74 : 120;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const facets = ["Data", "Process", "People", "Technology", "Strategy"];
  /** Place labels outside the stroke so the arc does not cover text */
  const labelRadius = r + stroke / 2 + (compact ? 34 : 48);
  const outerPad = compact ? 56 : 120;

  return (
    <div
      className={`pdf-readiness-ring relative flex items-center justify-center overflow-visible ${
        compact ? "pdf-readiness-ring--compact" : ""
      }`}
      style={{ width: size + outerPad, height: size + outerPad }}
    >
      <div
        className="pdf-ring-glow pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: size + (compact ? 32 : 80),
          height: size + (compact ? 32 : 80),
          background: "radial-gradient(circle, rgba(0,87,255,0.25) 0%, transparent 65%)",
          filter: compact ? "none" : "blur(20px)",
        }}
      />

      <svg
        className="relative z-[1]"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0057FF" />
            <stop offset="100%" stopColor="#0A84FF" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(7,17,31,0.06)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#${gradientId})`}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: "drop-shadow(0 0 16px rgba(10,132,255,0.55))" }}
        />
      </svg>

      {facets.map((label, i) => {
        const angle = (i / facets.length) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(angle) * labelRadius;
        const y = Math.sin(angle) * labelRadius;
        const isTop = y < -labelRadius * 0.55;

        return (
          <div
            key={label}
            className="pdf-readiness-ring__facet absolute left-1/2 top-1/2 z-[5]"
            style={{
              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
            }}
          >
            <div
              className={`flex flex-col items-center gap-1 ${
                isTop ? "flex-col-reverse" : ""
              }`}
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--electric)] shadow-[0_0_10px_rgba(0,87,255,0.75)]" />
              <span
                className={`pdf-readiness-ring__facet-label text-center font-medium uppercase text-foreground/70 ${
                  compact ? "text-[8px] tracking-[0.14em]" : "text-[11px] tracking-[0.18em]"
                }`}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}

      <div
        className={`pdf-readiness-ring__center absolute left-1/2 top-1/2 z-10 flex flex-col items-center justify-center ${
          compact ? "pdf-readiness-ring__center--compact" : ""
        }`}
        style={{
          transform: compact ? "translate(-50%, calc(-50% + 6px))" : "translate(-50%, -50%)",
        }}
      >
        <span
          className={`pdf-readiness-ring__value font-display font-semibold tracking-tight text-foreground ${
            compact ? "text-[40px] leading-none" : "text-6xl leading-none"
          }`}
        >
          {value}%
        </span>
        <span
          className={`pdf-readiness-ring__label uppercase text-[var(--electric)] ${
            compact
              ? "text-[8px] font-semibold tracking-[0.16em]"
              : "mt-1 text-[11px] font-medium tracking-[0.22em]"
          }`}
        >
          {readinessLabel} Readiness
        </span>
      </div>
    </div>
  );
}
